import _classNames from 'clsx';
import * as React from 'react';
import { Component, createRef } from 'react';
import Button from '../../button/button';
import Callout from '../../callout/callout';
import ComplexIcon from '../../icons/ComplexIcon/ComplexIcon';
import Loader from '../../loader/loader';

// Define FILETYPE enum only once
export enum FILETYPE {
  CSV = 'text/csv',
}

// TODO: how to render prop data in storybook docs?

interface UploadAreaProps {
  /**
   * The type of files allowed to be selected for upload
   */
  acceptFileType: FILETYPE;
  /**
   * The button text/CTA displayed to user to confirm upload; if no text is provided, CTA will read as "Upload File"
   */
  actionText?: string;
  /**
   * Set to `true` if there is a problem with the file the user supplied; this will display the button to "Select new (file type)"
   */
  fileNotCompatible?: boolean;
  /**
   * The instructional copy for the user
   */
  infoText?: string;
  /**
   * Set to `true` to render in a loading state while upload is in progress
   */
  isUploading?: boolean;
  /**
   * A callback function that will run when a file is dropped into the UploadArea
   */
  onDrop?: () => void;
  /**
   * A callback function that will run when the file data in the component is updated
   */
  onFileChange?: (arg: object) => void;
  /**
   * A callback function that will run when the user drops an incompatible file
   */
  onFileTypeError?: (arg: string) => void;
  /**
   * A callback function that will run when the user drops multiple files in the UploadArea
   */
  onMultipleFiles?: (arg: number) => void;
  /**
   * A callback function that will run when the user clicks to remove an uploaded file
   */
  onRemoveFile?: () => void;
  /**
   * A callback function that will run when the user clicks to replace a selected file
   */
  onReplace?: () => void;
  /**
   * A callback function that will run when the user clicks to select a file (initial state)
   */
  onSelectFile?: () => void;
  /**
   * A callback function that will run when the user clicks to select a new file (when `fileNotCompatible` is true)
   */
  onSelectNew?: () => void;
  /**
   * A callback function that will run when the upload is triggered
   */
  onUpload: (arg: object) => void;
  /**
   * Will render as an error message (overrides any validation error rendered by the component)
   */
  uploadError?: string;
  /**
   * Set this to `true` after the onUpload function has been called and the upload has been successful
   */
  uploadSuccessful?: boolean;
}

interface UploadAreaState {
  dragging: boolean;
  dragCounter: number;
  error: string;
  fileData: File | { name: string; size: number } | null;
  showLoader: boolean;
}

/**
 * With this component, a user can drag and drop a file to upload.
 */
class UploadArea extends Component<UploadAreaProps, UploadAreaState> {
  static FILETYPE_TO_STRING_MAP = {
    [FILETYPE.CSV]: 'CSV',
  };

  private dropRef = createRef<HTMLDivElement>();

  // exposed for testing
  fileInput = createRef<HTMLInputElement>();

  constructor(props: UploadAreaProps) {
    super(props);
    this.state = {
      dragCounter: 0,
      dragging: false,
      error: '',
      fileData: null,
      showLoader: false,
    };
  }

  componentDidMount() {
    const div = this.dropRef.current;
    div?.addEventListener('dragenter', this.handleDragEvent);
    div?.addEventListener('dragleave', this.handleDragEvent);
    div?.addEventListener('dragover', this.handleDragEvent);
    div?.addEventListener('drop', this.handleDrop);
  }

  componentDidUpdate(prevProps: UploadAreaProps, prevState: UploadAreaState) {
    const { fileData } = this.state;
    const { onFileChange = () => {} } = this.props;
    if (onFileChange && fileData && fileData?.name !== prevState.fileData?.name) {
      onFileChange(fileData);
    }
  }

  componentWillUnmount() {
    const div = this.dropRef.current;
    div?.removeEventListener('dragenter', this.handleDragEvent);
    div?.removeEventListener('dragleave', this.handleDragEvent);
    div?.removeEventListener('dragover', this.handleDragEvent);
    div?.removeEventListener('drop', this.handleDrop);
  }

  handleDragEvent = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // dragover event type must be included to prevent browser default behavior (opening or downloading file)
    if (e.type === 'dragover') return;

    const { dragCounter } = this.state;
    const { uploadSuccessful } = this.props;
    if (uploadSuccessful) return;

    if (e.type === 'dragenter' && e.dataTransfer?.items && e.dataTransfer?.items.length > 0) {
      this.setState((state) => ({
        dragCounter: state.dragCounter + 1,
        dragging: true,
      }));
    } else if (e.type === 'dragleave' && dragCounter === 1) {
      this.setState({
        dragCounter: 0,
        dragging: false,
      });
    } else if (e.type === 'dragleave' && dragCounter > 1) {
      this.setState((state) => ({
        dragCounter: state.dragCounter - 1,
      }));
    }
  };

  static getFileSizeString = (size: number) => {
    let byteValue: string = '';
    let unit: string = '';
    if (size < 1000) {
      byteValue = Math.round(size).toString();
      unit = 'B';
    } else if (size >= 1000 && size < 1000000) {
      byteValue = Math.round(size / 1000).toString();
      unit = 'KB';
    } else if (size >= 1000000 && size < 1000000000) {
      byteValue = Math.round(size / 1000000).toString();
      unit = 'MB';
    } else if (size >= 1000000000) {
      byteValue = Math.round(size / 1000000000).toString();
      unit = 'GB';
    }
    return `${byteValue} ${unit}`;
  };

  handleDrop = (e: DragEvent) => {
    const {
      acceptFileType,
      uploadSuccessful,
      onDrop = () => {},
      onMultipleFiles = () => {},
      onFileTypeError = () => {},
    } = this.props;
    e.preventDefault();
    e.stopPropagation();
    if (uploadSuccessful) return;
    this.setState({ dragging: false });

    if (
      e.dataTransfer?.files &&
      e.dataTransfer?.files.length === 1 &&
      e.dataTransfer?.files[0].type === acceptFileType
    ) {
      onDrop();
      if (this.fileInput.current && e.dataTransfer) {
        this.fileInput.current.files = e.dataTransfer.files;
      }
      this.handleFiles();
      this.setState({
        dragCounter: 0,
        error: '',
      });
      e.dataTransfer?.clearData();
    } else if (e.dataTransfer?.files && e.dataTransfer?.files.length > 1) {
      this.setState({
        error: `Cannot read multiple files.  Please upload (1) ${UploadArea.FILETYPE_TO_STRING_MAP[acceptFileType]} file at a time.`,
      });
      onMultipleFiles(e.dataTransfer?.files.length);
    } else if (e.dataTransfer?.files && e.dataTransfer?.files[0].type !== acceptFileType) {
      this.setState({
        error: `Unsupported file type. Supported files should include the ".${UploadArea.FILETYPE_TO_STRING_MAP[
          acceptFileType
        ].toLowerCase()}" file extension.`,
      });
      // passing file extension as arg
      const fileExtension = e.dataTransfer?.files[0].name.split('.').pop() || '';
      onFileTypeError(fileExtension);
    }
  };

  handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { onUpload } = this.props;
    const { fileData } = this.state;
    // Add null check before passing to onUpload
    if (fileData) {
      onUpload(fileData as object);
    }
  };

  handleFiles = () => {
    const { fileNotCompatible, onRemoveFile = () => {} } = this.props;
    this.setState({ error: '' });

    // Refine null check for fileInput.current and its files property
    const currentInput = this.fileInput.current;
    if (!currentInput || !currentInput.files || currentInput.files.length === 0) {
      return;
    }

    this.setState({
      showLoader: true,
      // Use the non-null currentInput.files here
      fileData: currentInput.files[0] || null,
    });

    if (fileNotCompatible) {
      onRemoveFile();
    }
    // using generic setTimeout to show loader animation once as visual feedback for the user
    setTimeout(() => this.setState({ showLoader: false }), 1000);
  };

  handleRemoveFile = () => {
    const { onRemoveFile = () => {} } = this.props;
    this.setState({ fileData: null });
    if (this.fileInput.current) {
      this.fileInput.current.value = '';
    }
    onRemoveFile();
  };

  handleReplaceFile = (e: MouseEvent) => {
    const { onReplace = () => {} } = this.props;
    onReplace();
    UploadArea.handleFileButton(e);
  };

  handleSelectFile = (e: MouseEvent) => {
    const { onSelectFile = () => {} } = this.props;
    onSelectFile();
    UploadArea.handleFileButton(e);
  };

  handleSelectNew = (e: MouseEvent) => {
    const { onSelectNew = () => {} } = this.props;
    onSelectNew();
    UploadArea.handleFileButton(e);
  };

  static handleFileButton = (e: KeyboardEvent | MouseEvent) => {
    // keyCode only exists in KeyboardEvent and button only exists in MouseEvent
    // 13 is return/enter, 32 is space
    if (('keyCode' in e && (e.keyCode === 13 || e.keyCode === 32)) || 'button' in e) {
      e.preventDefault();
      const fileInput = document.getElementById('fileinput');
      fileInput?.click();
    }
  };

  render() {
    const {
      infoText,
      isUploading,
      actionText,
      acceptFileType,
      fileNotCompatible,
      uploadError,
      uploadSuccessful,
    } = this.props;
    const { dragging, fileData, error, showLoader } = this.state;

    const fileinputDomId = 'fileinput';

    return (
      <>
        <div
          className={_classNames('UploadArea', {
            'UploadArea--dragging': dragging,
            'UploadArea--hasFileData': fileData,
          })}
          ref={this.dropRef}
        >
          <Loader isLoading={showLoader} classes="UploadArea__loader" />
          <div className="UploadArea__form">
            {fileData ? (
              <div className="UploadArea__fileData">
                <ComplexIcon name="csv-icon" classes="UploadArea__fileIcon" />
                <div>
                  <p className="UploadArea__fileData__name">{fileData.name}</p>
                  <p className="UploadArea__fileData__size">
                    {UploadArea.getFileSizeString(fileData.size)}
                  </p>
                </div>
              </div>
            ) : (
              <div className="UploadArea__instructions">
                {infoText || 'Select a file or drag and drop here.'}
              </div>
            )}
            <div>
              <input
                className="UploadArea__input"
                type="file"
                id={fileinputDomId}
                name="fileinput"
                accept={acceptFileType || FILETYPE.CSV}
                ref={this.fileInput}
                onChange={this.handleFiles}
              />
              {fileData && !uploadSuccessful && (
                <>
                  <Button
                    type="secondary"
                    classes="UploadArea__button--fileReady"
                    buttonProps={{ type: 'button' }}
                    onKeyDown={UploadArea.handleFileButton}
                    onClick={this.handleReplaceFile}
                  >
                    <label htmlFor={fileinputDomId} className="UploadArea__button">
                      Replace file
                    </label>
                  </Button>
                  <Button
                    buttonProps={{ type: 'submit' }}
                    type="primary"
                    onClick={this.handleSubmit}
                    classes="UploadArea__button--fileReady"
                    loading={isUploading}
                  >
                    {actionText || 'Upload file'}
                  </Button>
                </>
              )}
              {!fileData && !uploadSuccessful && (
                <Button
                  type="primary"
                  buttonProps={{ type: 'button' }}
                  onClick={this.handleSelectFile}
                  classes="UploadArea__button"
                >
                  {/* eslint jsx-a11y/label-has-associated-control: ["error", { assert: "either" } ] */}
                  <label htmlFor={fileinputDomId} className="UploadArea__button">
                    Select file
                  </label>
                </Button>
              )}
              {fileData && uploadSuccessful && !fileNotCompatible && (
                <Button
                  type="destructiveMinor"
                  buttonProps={{ type: 'button' }}
                  onClick={this.handleRemoveFile}
                  classes="UploadArea__button"
                >
                  Remove file
                </Button>
              )}
              {fileData && uploadSuccessful && fileNotCompatible && (
                <Button
                  type="primary"
                  buttonProps={{ type: 'button' }}
                  onClick={this.handleSelectNew}
                  classes="UploadArea__button"
                >
                  <label htmlFor="fileinput" className="UploadArea__button">
                    Select new {UploadArea.FILETYPE_TO_STRING_MAP[acceptFileType]}
                  </label>
                </Button>
              )}
            </div>
          </div>
        </div>
        {(uploadError || error) && (
          <div className="UploadArea__errorMessage">
            <Callout
              text={uploadError || error}
              type={Callout.TYPE.ERROR}
              defaultIcon
              iconColor={Callout.THEME.COLOR}
            />
          </div>
        )}
      </>
    );
  }
}

export default UploadArea;
